import { supabase } from '../lib/supabase';
import {
  User,
  Company,
  Appointment,
  Service,
  Venue,
  AddOn,
  Customer,
  QuestionnaireSubmission,
  SystemConfig,
  SystemConfigUpdatePayload,
  MedicalCertificate,
  CertificateSigner,
  UserGroup,
  QuestionnaireTemplate,
  CompanyUser,
  OpayoPaymentRequest,
  OpayoPaymentResponse,
  OpayoPaymentStatusResponse,
  OpayoSessionKeyResponse,
  CheckoutQuoteRequest,
  CheckoutQuoteResponse,
  Payment,
  BookingAvailabilityResponse,
  ManualReminderResponse,
  ManualReminderType,
  PaginatedResponse,
  ActiveSession,
  CommunicationLog,
  CompanyCustomerSummary,
  AuditLog,
  VerifiedCertificate,
  CertificateVerificationResult,
  SageSyncQueueResponse,
  SageSyncJobStatusResponse,
  SageOauthStartResponse,
  HealthAssessment,
  AppointmentDocument,
  AppointmentDocumentType
} from '../types';
import { normalizeDateOnly } from '../lib/dates';
import { buildQueryString, isUnauthorizedError, toCamelCase } from '../lib/apiHelpers';
import { ApiError } from '../lib/apiError';
import { reportClientError } from '../lib/errorReporter';
import { type Provider } from '@supabase/supabase-js';
import { buildApiErrorMessage } from './apiErrorUtils';
import {
  buildCheckoutQuotePayload,
  buildCreateBookingPayload,
  buildOpayoPaymentPayload,
  buildUpdateCompanyPayload
} from './apiPayloadUtils';

export type LoginResult =
  | { error: string; user?: never; company?: never; customer?: never }
  | { user: User; error?: never; company?: never; customer?: never }
  | { company: Company; error?: never; user?: never; customer?: never }
  | { customer: Customer; error?: never; user?: never; company?: never };

const API_BASE = import.meta.env.VITE_API_URL
  ? import.meta.env.VITE_API_URL.replace(/\/$/, '')
  : '';

const AUTH_ERROR_EVENT = 'k2_toast';
let hasShownForbiddenToast = false;
let isHandlingUnauthorizedSignOut = false;

const clearSuppressSignOut = () => {
  try {
    localStorage.removeItem('k2_suppress_signed_out');
  } catch (_err) {
    // ignore storage errors
  }
};

const emitToast = (message: string) => {
  if (typeof window === 'undefined') return;
  if (hasShownForbiddenToast) return;
  hasShownForbiddenToast = true;
  window.dispatchEvent(
    new CustomEvent(AUTH_ERROR_EVENT, {
      detail: { message, type: 'error', durationMs: 5000 }
    })
  );
};

const handleForbidden = (message?: string) => {
  clearSuppressSignOut();
  emitToast(message || 'Access denied. Please sign in again.');
};

const getAuthHeader = async (accessToken?: string | null) => {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  }

  const {
    data: { session }
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
};

const handleUnauthorizedSignOut = async () => {
  if (isHandlingUnauthorizedSignOut) return;
  isHandlingUnauthorizedSignOut = true;
  try {
    markSuppressSignOut();
    await supabase.auth.signOut();
  } finally {
    isHandlingUnauthorizedSignOut = false;
  }
};

const createRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;

// Network failures throw raw (non-ApiError) errors on purpose — startupError
// classification keys on the absence of `status`. Attach the id we sent so the
// UI can still show a support reference.
const attachRequestId = (err: unknown, requestId: string) => {
  if (typeof err === 'object' && err !== null) {
    (err as { requestId?: string }).requestId = requestId;
  }
  return err;
};

const resolveResponseRequestId = (data: unknown, response: Response, sentId: string) => {
  if (data && typeof data === 'object') {
    const fromBody = (data as { requestId?: unknown }).requestId;
    if (typeof fromBody === 'string' && fromBody) return fromBody;
  }
  return response.headers.get('x-request-id') || sentId;
};

const createRequestHeaders = (options: RequestInit, extra: Record<string, string> = {}) => {
  const headers = new Headers(options.headers || {});

  Object.entries(extra).forEach(([name, value]) => {
    if (value) headers.set(name, value);
  });

  const hasBody = options.body !== undefined && options.body !== null;
  if (hasBody && typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
};

// timeoutMs aborts the request after the given time and surfaces a 408 ApiError.
// Payment-flow calls set it so a hung request can never strand the checkout on an
// infinite spinner — the UI recovers via the payment-status endpoint instead.
const apiFetch = async (
  path: string,
  options: RequestInit & { timeoutMs?: number } = {},
  accessToken?: string | null
) => {
  const requestId = createRequestId();
  const authHeader = await getAuthHeader(accessToken);
  const { headers: _headers, timeoutMs, ...restOptions } = options;
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: createRequestHeaders(options, { ...authHeader, 'x-request-id': requestId }),
      ...restOptions,
      ...(controller ? { signal: controller.signal } : {})
    });
  } catch (err) {
    reportClientError({
      level: 'error',
      message: `API ${(options.method || 'GET').toUpperCase()} ${path} network failure: ${
        err instanceof Error ? err.message : String(err)
      }`,
      requestId
    });
    if (controller?.signal.aborted) {
      throw new ApiError('The request timed out. Please check your connection.', 408, requestId);
    }
    throw attachRequestId(err, requestId);
  } finally {
    if (timer !== null) window.clearTimeout(timer);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedSignOut();
    }
    if (response.status === 403) {
      handleForbidden(buildApiErrorMessage(response.status, data, response.statusText));
    }
    if (response.status >= 500) {
      reportClientError({
        level: 'error',
        message: `API ${(options.method || 'GET').toUpperCase()} ${path} failed with ${response.status}`,
        requestId: resolveResponseRequestId(data, response, requestId)
      });
    }
    throw new ApiError(
      buildApiErrorMessage(response.status, data, response.statusText),
      response.status,
      resolveResponseRequestId(data, response, requestId),
      (data as { details?: Record<string, unknown> | null })?.details ?? null
    );
  }
  return data;
};

const apiFetchCsv = async (
  path: string,
  options: RequestInit = {},
  accessToken?: string | null
) => {
  const requestId = createRequestId();
  const authHeader = await getAuthHeader(accessToken);
  const { headers: _headers, ...restOptions } = options;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: createRequestHeaders(options, { ...authHeader, 'x-request-id': requestId }),
      ...restOptions
    });
  } catch (err) {
    throw attachRequestId(err, requestId);
  }
  const data = await response.text().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedSignOut();
    }
    if (response.status === 403) {
      handleForbidden(buildApiErrorMessage(response.status, data, response.statusText));
    }
    throw new ApiError(
      buildApiErrorMessage(response.status, data, response.statusText),
      response.status,
      response.headers.get('x-request-id') || requestId
    );
  }
  return data;
};

const apiFetchPublic = async (path: string, options: RequestInit = {}) => {
  const requestId = createRequestId();
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: createRequestHeaders(options, { 'x-request-id': requestId }),
      ...options
    });
  } catch (err) {
    throw attachRequestId(err, requestId);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 403) {
      handleForbidden(buildApiErrorMessage(response.status, data, response.statusText));
    }
    throw new ApiError(
      buildApiErrorMessage(response.status, data, response.statusText),
      response.status,
      resolveResponseRequestId(data, response, requestId)
    );
  }
  return data;
};

type ListQueryParams = Record<string, string | number | boolean | undefined>;

const buildListPath = (path: string, params: ListQueryParams) => {
  const query = buildQueryString(params);
  return query ? `${path}?${query}` : path;
};

const fetchPaginatedList = async <T>(
  path: string,
  params: ListQueryParams
): Promise<PaginatedResponse<T>> => {
  const data = await apiFetch(buildListPath(path, params));
  return {
    items: (toCamelCase(data.items || []) as T[]) || [],
    totalCount: Number(data.totalCount || 0),
    page: Number(data.page || 1),
    pageSize: Number(data.pageSize || 0)
  };
};

const fetchAllList = async <T>(path: string, params: ListQueryParams): Promise<string | {}> => {
  const data = await apiFetchCsv(buildListPath(path, params), { method: 'POST' });
  return data;
};

const fetchList = async <T>(path: string, params: ListQueryParams): Promise<T[]> => {
  const data = await apiFetch(buildListPath(path, params));
  return (toCamelCase(data || []) as T[]) || [];
};

const toCamelArray = <T>(value: unknown): T[] => {
  if (!Array.isArray(value)) return [];
  return toCamelCase(value) as T[];
};

interface PublicReferenceData {
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
}

interface InitialData {
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  users: User[];
  companies: Company[];
  customers: Customer[];
  userGroups: UserGroup[];
  submissions: QuestionnaireSubmission[];
  certificates: MedicalCertificate[];
  payments: Payment[];
  systemConfig: SystemConfig | undefined;
  questionnaires: QuestionnaireTemplate[];
  errors: Record<string, string>;
}

const markSuppressSignOut = () => {
  try {
    localStorage.setItem('k2_suppress_signed_out', '1');
  } catch (_err) {
    // ignore storage errors
  }
};

export const api = {
  fetchInitialData: async (): Promise<InitialData> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();

    let endpoint = '/api/public/init';
    let usedSession = false;
    if (session) {
      const userType = session.user.user_metadata?.user_type || 'staff';
      if (userType === 'staff') endpoint = '/api/init';
      else if (userType === 'company') endpoint = '/api/company/init';
      else if (userType === 'customer') endpoint = '/api/customer/init';
      usedSession = true;
    }

    let data;
    try {
      data = await apiFetch(endpoint);
    } catch (error) {
      if (usedSession && isUnauthorizedError(error)) {
        markSuppressSignOut();
        await supabase.auth.signOut();
        data = await apiFetchPublic('/api/public/init');
      } else {
        throw error;
      }
    }
    const dbConfig = data?.systemConfig
      ? (toCamelCase(data.systemConfig) as SystemConfig)
      : undefined;

    return {
      appointments: toCamelArray<Appointment>(data.appointments || []),
      services: toCamelArray<Service>(data.services || []),
      venues: toCamelArray<Venue>(data.venues || []),
      addOns: toCamelArray<AddOn>(data.addOns || []),
      users: toCamelArray<User>(data.users || []),
      companies: toCamelArray<Company>(data.companies || []),
      customers: toCamelArray<Customer>(data.customers || []),
      userGroups: toCamelArray<UserGroup>(data.userGroups || []),
      submissions: toCamelArray<QuestionnaireSubmission>(data.submissions || []),
      certificates: toCamelArray<MedicalCertificate>(data.certificates || []),
      payments: toCamelArray<Payment>(data.payments || []),
      systemConfig: dbConfig,
      questionnaires: toCamelArray<QuestionnaireTemplate>(data.questionnaires || []),
      errors: data.errors || {}
    };
  },

  fetchPublicReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetchPublic('/api/public/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  fetchCompanyReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetch('/api/company/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  fetchCustomerReference: async (): Promise<PublicReferenceData> => {
    const data = await apiFetch('/api/customer/reference');
    return {
      venues: toCamelArray<Venue>(data.venues || []),
      services: toCamelArray<Service>(data.services || []),
      addOns: toCamelArray<AddOn>(data.addOns || [])
    };
  },

  getSystemConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetch('/api/config');
    return toCamelCase(data?.systemConfig || {}) as SystemConfig;
  },

  getPublicConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetchPublic('/api/config/public');
    return toCamelCase(data?.systemConfig || {}) as SystemConfig;
  },

  listAuditLogs: (params: ListQueryParams): Promise<PaginatedResponse<AuditLog>> =>
    fetchPaginatedList('/api/audit-logs', params),
  listStaffServices: (params: ListQueryParams): Promise<PaginatedResponse<Service>> =>
    fetchPaginatedList('/api/staff/services', params),
  listStaffAddOns: (params: ListQueryParams): Promise<PaginatedResponse<AddOn>> =>
    fetchPaginatedList('/api/staff/addons', params),
  listStaffVenues: (params: ListQueryParams): Promise<PaginatedResponse<Venue>> =>
    fetchPaginatedList('/api/staff/venues', params),
  listStaffQuestionnaires: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireTemplate>> =>
    fetchPaginatedList('/api/staff/questionnaires', params),
  listStaffUserGroups: (params: ListQueryParams): Promise<PaginatedResponse<UserGroup>> =>
    fetchPaginatedList('/api/staff/user-groups', params),
  listStaffUsers: (params: ListQueryParams): Promise<PaginatedResponse<User>> =>
    fetchPaginatedList('/api/staff/users', params),
  listStaffCustomers: (params: ListQueryParams): Promise<PaginatedResponse<Customer>> =>
    fetchPaginatedList('/api/staff/customers', params),
  getStaffCustomer: async (customerId: string): Promise<Customer> => {
    const data = await apiFetch(`/api/staff/customers/${customerId}`);
    return toCamelCase(data?.customer || {}) as Customer;
  },
  listStaffCustomerHistory: (
    customerId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList(`/api/staff/customers/${customerId}/history`, params),
  listStaffCustomerLogs: (
    customerId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CommunicationLog>> =>
    fetchPaginatedList(`/api/staff/customers/${customerId}/logs`, params),
  listStaffCompanies: (params: ListQueryParams): Promise<PaginatedResponse<Company>> =>
    fetchPaginatedList('/api/staff/companies', params),
  listCompanyTeam: (
    companyId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CompanyUser>> =>
    fetchPaginatedList(`/api/staff/companies/${companyId}/team`, params),
  listCompanyCustomers: (
    companyId: string,
    params: ListQueryParams
  ): Promise<PaginatedResponse<CompanyCustomerSummary>> =>
    fetchPaginatedList(`/api/staff/companies/${companyId}/customers`, params),
  listStaffSessions: (params: ListQueryParams): Promise<ActiveSession[]> =>
    fetchList('/api/staff/sessions', params),

  revokeStaffSession: async (id: string, reason?: string): Promise<{ success: boolean }> => {
    await apiFetch(`/api/staff/sessions/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || null
      })
    });
    return { success: true };
  },

  revokeCurrentSession: async (reason?: string): Promise<{ success: boolean }> => {
    await apiFetch('/api/session/revoke', {
      method: 'POST',
      body: JSON.stringify({
        reason: reason || null
      })
    });
    return { success: true };
  },

  listStaffBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/staff/bookings', params),
  listStaffAllBookings: (params: ListQueryParams): Promise<string | {}> =>
    fetchAllList('/api/staff/bookings/export-csv', params),
  listCompanyBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/company/bookings', params),
  listCustomerBookings: (params: ListQueryParams): Promise<PaginatedResponse<Appointment>> =>
    fetchPaginatedList('/api/customer/bookings', params),
  listStaffPayments: (params: ListQueryParams): Promise<PaginatedResponse<Payment>> =>
    fetchPaginatedList('/api/staff/payments', params),
  queueStaffSageSyncJob: async (bookingIds: string[]): Promise<SageSyncQueueResponse> => {
    const normalizedBookingIds = Array.isArray(bookingIds)
      ? [...new Set(bookingIds.map((id) => String(id || '').trim()).filter(Boolean))]
      : [];
    if (normalizedBookingIds.length === 0) {
      throw new Error('At least one booking must be selected.');
    }
    const data = await apiFetch('/api/staff/bookings/sage-sync/jobs', {
      method: 'POST',
      body: JSON.stringify({ bookingIds: normalizedBookingIds })
    });
    return toCamelCase(data || {}) as SageSyncQueueResponse;
  },
  getStaffSageSyncJobStatus: async (jobId: string): Promise<SageSyncJobStatusResponse> => {
    const normalizedJobId = String(jobId || '').trim();
    if (!normalizedJobId) throw new Error('Job id is required.');
    const data = await apiFetch(
      `/api/staff/bookings/sage-sync/jobs/${encodeURIComponent(normalizedJobId)}`
    );
    return toCamelCase(data || {}) as SageSyncJobStatusResponse;
  },
  listStaffCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/staff/certificates', params),
  listCompanyCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/company/certificates', params),
  listCustomerCertificates: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<MedicalCertificate>> =>
    fetchPaginatedList('/api/customer/certificates', params),

  verifyCertificatePublic: async (
    certificateId: string,
    name?: string
  ): Promise<CertificateVerificationResult> => {
    const id = String(certificateId || '').trim();
    if (!id) {
      throw new Error('Certificate ID is required');
    }
    const nameText = String(name || '').trim();
    const suffix = buildQueryString({ name: nameText || undefined });
    const querySuffix = suffix ? `?${suffix}` : '';
    const data = await apiFetchPublic(
      `/api/certificates/verify/${encodeURIComponent(id)}${querySuffix}`
    );
    const lifecycleStatusRaw = String(data.lifecycleStatus || 'valid');
    const lifecycleStatus: 'valid' | 'revoked' | 'expired' =
      lifecycleStatusRaw === 'revoked' || lifecycleStatusRaw === 'expired'
        ? lifecycleStatusRaw
        : 'valid';
    return {
      certificate: toCamelCase(data.certificate || {}) as VerifiedCertificate,
      lifecycleStatus,
      requiresNameConfirmation: Boolean(data.requiresNameConfirmation),
      nameConfirmed: Boolean(data.nameConfirmed)
    };
  },

  listStaffSubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/staff/submissions', params),
  listCompanySubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/company/submissions', params),
  listCustomerSubmissions: (
    params: ListQueryParams
  ): Promise<PaginatedResponse<QuestionnaireSubmission>> =>
    fetchPaginatedList('/api/customer/submissions', params),

  getDefaultSystemConfig: async (): Promise<SystemConfig> => {
    const data = await apiFetch('/api/config/defaults');
    return toCamelCase(data || {}) as SystemConfig;
  },

  getCurrentSession: async () => {
    if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
      try {
        return (await apiFetch('/api/session')) || null;
      } catch (e) {
        return null;
      }
    }

    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session) return null;

    try {
      const sessionProfile = await apiFetch('/api/session');
      if (sessionProfile?.type === 'company') {
        const status = String(sessionProfile.profile?.status || '').toLowerCase();
        if (['pending', 'on_hold', 'rejected'].includes(status)) {
          markSuppressSignOut();
          await supabase.auth.signOut();
          return null;
        }
      }
      return sessionProfile || null;
    } catch (e) {
      return null;
    }
  },

  login: async (
    email: string,
    pass: string,
    context: 'staff' | 'company' | 'customer'
  ): Promise<LoginResult> => {
    const cleanEmail = email.trim().toLowerCase();

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: pass
      });

      if (authError) {
        return { error: authError.message || 'Invalid credentials.' };
      }

      const sessionProfile = await apiFetch('/api/session', {}, authData.session?.access_token);
      if (sessionProfile && sessionProfile.type === context) {
        if (sessionProfile.type === 'company') {
          const status = String(sessionProfile.profile?.status || '').toLowerCase();
          if (['pending', 'on_hold', 'rejected'].includes(status)) {
            markSuppressSignOut();
            await supabase.auth.signOut();
            const message =
              status === 'rejected'
                ? 'Your company account was rejected. Please contact support.'
                : status === 'on_hold'
                  ? 'Your company account is on hold. Please contact support.'
                  : 'Your company account is pending approval. Please wait for admin confirmation or contact support.';
            return { error: message };
          }
        }
        return sessionProfile.type === 'staff'
          ? { user: sessionProfile.profile }
          : sessionProfile.type === 'company'
            ? { company: sessionProfile.profile }
            : { customer: sessionProfile.profile };
      }

      if (sessionProfile?.type) {
        markSuppressSignOut();
        await supabase.auth.signOut();

        const contextLabel =
          context === 'staff'
            ? 'Staff Portal'
            : context === 'company'
              ? 'Company Portal'
              : 'Patient Portal';
        return { error: `This account does not belong to ${contextLabel}.` };
      }

      markSuppressSignOut();
      await supabase.auth.signOut();
      return { error: 'Login succeeded but no profile was found for this account.' };
    } catch (e: unknown) {
      return {
        error: e instanceof Error ? e.message : 'An unexpected error occurred during login.'
      };
    }
  },

  devBypassLogin: async (): Promise<LoginResult> => {
    const sessionProfile = await apiFetch('/api/session');
    if (sessionProfile?.type === 'staff') {
      return { user: sessionProfile.profile as User };
    }
    return { error: 'Dev bypass: staff profile not found' };
  },

  loginWithMicrosoft: async () => {
    const provider = String(import.meta.env.VITE_SSO_PROVIDER || 'azure').trim();
    const configuredRedirect = String(import.meta.env.VITE_SSO_REDIRECT_URL || '').trim();
    const baseRedirect =
      configuredRedirect || `${window.location.origin}${window.location.pathname}`;
    const redirectTo = `${baseRedirect}${baseRedirect.includes('?') ? '&' : '?'}sso=1`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: provider as Provider,
      options: { redirectTo, scopes: 'openid profile email' }
    });

    if (error) {
      throw error;
    }
  },

  createStaffUser: async (user: User) => {
    await apiFetch('/api/staff-users', {
      method: 'POST',
      body: JSON.stringify(user)
    });
    return { success: true };
  },

  deleteUser: async (id: string) => {
    await apiFetch(`/api/users/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  resetStaffPassword: async (id: string, password?: string) => {
    await apiFetch(`/api/staff-users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({
        password,
        redirectTo: `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  registerCompany: async (company: Company) => {
    await apiFetch('/api/company/register', {
      method: 'POST',
      body: JSON.stringify({
        name: company.name,
        contactPerson: company.contactPerson,
        email: company.email,
        address: company.address,
        phone: company.phone || null
      })
    });
    return { success: true };
  },

  createCompanyWithAuth: async (company: Company) => {
    await apiFetch('/api/companies', {
      method: 'POST',
      body: JSON.stringify({
        name: company.name,
        contactPerson: company.contactPerson,
        email: company.email,
        address: company.address,
        phone: company.phone || null,
        status: company.status,
        tier: company.tier,
        paymentPreference: company.paymentPreference
      })
    });
    return { success: true };
  },

  createCompanyMember: async (companyId: string, member: Partial<CompanyUser>) => {
    await apiFetch('/api/company-users', {
      method: 'POST',
      body: JSON.stringify({
        companyId,
        name: member.name,
        email: member.email,
        receiveNotifications: member.receiveNotifications
      })
    });
    return { success: true };
  },

  updateCompanyMember: async (memberId: string, updates: Partial<CompanyUser>) => {
    await apiFetch(`/api/company-users/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: updates.name,
        email: updates.email,
        receiveNotifications: updates.receiveNotifications
      })
    });
    return { success: true };
  },

  setCompanyMemberPrimary: async (
    companyId: string,
    memberId: string
  ): Promise<{ success: boolean; company?: Pick<Company, 'id' | 'contactPerson' | 'email'> }> => {
    const data = await apiFetch(`/api/staff/companies/${companyId}/team/${memberId}/primary`, {
      method: 'POST'
    });

    return {
      success: true,
      company: data?.company
        ? ({
            id: data.company.id,
            contactPerson: data.company.contactPerson,
            email: data.company.email
          } as Pick<Company, 'id' | 'contactPerson' | 'email'>)
        : undefined
    };
  },

  deactivateCompanyMember: async (companyId: string, memberId: string) => {
    await apiFetch(`/api/staff/companies/${companyId}/team/${memberId}/deactivate`, {
      method: 'POST'
    });
    return { success: true };
  },

  reactivateCompanyMember: async (companyId: string, memberId: string) => {
    await apiFetch(`/api/staff/companies/${companyId}/team/${memberId}/reactivate`, {
      method: 'POST'
    });
    return { success: true };
  },

  deleteCompanyMember: async (memberId: string) => {
    await apiFetch(`/api/company-users/${memberId}`, { method: 'DELETE' });
    return { success: true };
  },

  resetCompanyMemberPassword: async (memberId: string) => {
    await apiFetch(`/api/company-users/${memberId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({
        redirectTo: `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  createCustomerUser: async (customer: Customer) => {
    await apiFetch('/api/customers', {
      method: 'POST',
      body: JSON.stringify(customer)
    });
    return { success: true };
  },

  logout: async () => {
    try {
      await api.revokeCurrentSession('User logout');
    } catch (_err) {
      // Keep local sign-out resilient if session endpoint fails.
    }
    await supabase.auth.signOut();
  },

  uploadFile: async (file: File, bucket: string): Promise<string> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    const authHeader: Record<string, string> = {};
    if (session?.access_token) {
      authHeader.Authorization = `Bearer ${session.access_token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);
    const response = await fetch(`${API_BASE}/api/uploads/logo`, {
      method: 'POST',
      headers: authHeader,
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response.status, data, response.statusText));
    }
    if (!data?.publicUrl) {
      throw new Error('Upload failed.');
    }
    return data.publicUrl;
  },

  createBooking: async (booking: Appointment) => {
    const payload = buildCreateBookingPayload(booking);
    await apiFetch('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: 20000
    });
    return { success: true };
  },

  getBookingAvailability: async ({
    venueId,
    date,
    serviceId,
    serviceIds,
    addOnIds = [],
    excludeAppointmentId
  }: {
    venueId: string;
    date: string | Date;
    serviceId: string;
    serviceIds?: string[];
    addOnIds?: string[];
    excludeAppointmentId?: string;
  }): Promise<BookingAvailabilityResponse> => {
    const normalizedDate = normalizeDateOnly(date);
    const data = await apiFetch('/api/bookings/availability', {
      method: 'POST',
      body: JSON.stringify({
        venueId,
        date: normalizedDate || date,
        serviceId,
        ...(serviceIds && serviceIds.length > 0 ? { serviceIds } : {}),
        addOnIds,
        excludeAppointmentId
      })
    });
    return toCamelCase(data) as BookingAvailabilityResponse;
  },

  createOpayoSessionKey: async (): Promise<OpayoSessionKeyResponse> => {
    const data = await apiFetch('/api/payments/opayo/session-key', {
      method: 'POST',
      body: JSON.stringify({}),
      timeoutMs: 15000
    });
    return data as OpayoSessionKeyResponse;
  },

  processOpayoPayment: async (payload: OpayoPaymentRequest): Promise<OpayoPaymentResponse> => {
    const normalizedPayload = buildOpayoPaymentPayload(payload);
    const data = await apiFetch('/api/payments/opayo/charge', {
      method: 'POST',
      body: JSON.stringify(normalizedPayload),
      // Opayo can be slow; aborting too early turns a successful charge into an
      // "unknown outcome" for the user, so give it a generous ceiling.
      timeoutMs: 60000
    });
    return data as OpayoPaymentResponse;
  },

  getOpayoPaymentStatus: async ({
    paymentId,
    paymentLinkToken,
    email
  }: {
    paymentId: string;
    paymentLinkToken?: string;
    email?: string;
  }): Promise<OpayoPaymentStatusResponse> => {
    const params = new URLSearchParams();
    if (paymentLinkToken) params.set('token', paymentLinkToken);
    if (email) params.set('email', email);
    const query = params.toString();
    const data = await apiFetch(
      `/api/payments/${encodeURIComponent(paymentId)}/status${query ? `?${query}` : ''}`,
      { method: 'GET', timeoutMs: 10000 }
    );
    return data as OpayoPaymentStatusResponse;
  },

  getCheckoutQuote: async (payload: CheckoutQuoteRequest): Promise<CheckoutQuoteResponse> => {
    const normalizedPayload = buildCheckoutQuotePayload(payload);
    const data = await apiFetch('/api/payments/quote', {
      method: 'POST',
      body: JSON.stringify(normalizedPayload),
      timeoutMs: 15000
    });
    return data as CheckoutQuoteResponse;
  },

  updateBooking: async (booking: Appointment, notify?: { email: boolean; sms: boolean }) => {
    const normalizedDate = normalizeDateOnly(booking.date);
    await apiFetch(`/api/bookings/${booking.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...booking,
        date: normalizedDate || booking.date,
        notify
      })
    });
    return { success: true };
  },

  patchBooking: async (
    id: string,
    updates: Partial<Appointment>,
    notify?: { email: boolean; sms: boolean }
  ) => {
    if (!id) throw new Error('Booking id is required.');
    const body: Record<string, unknown> = { ...updates };
    if (typeof updates.date === 'string') {
      const normalizedDate = normalizeDateOnly(updates.date);
      body.date = normalizedDate || updates.date;
    }
    if (notify) body.notify = notify;
    await apiFetch(`/api/bookings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return { success: true };
  },

  deleteBooking: async (id: string) => {
    if (!id) throw new Error('Deletion ID is missing.');
    await apiFetch(`/api/bookings/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  triggerReminder: async (
    appointmentId: string,
    type: ManualReminderType = 'both',
    templateType?: string
  ): Promise<ManualReminderResponse> => {
    const data = await apiFetch(`/api/reminders/trigger/${appointmentId}`, {
      method: 'POST',
      body: JSON.stringify(
        templateType
          ? {
              type,
              templateType
            }
          : { type }
      )
    });
    return data as ManualReminderResponse;
  },

  sendQuestionnaire: async (appointmentId: string): Promise<{ success: boolean }> => {
    const data = await apiFetch(`/api/bookings/${appointmentId}/send-questionnaire`, {
      method: 'POST'
    });
    return data as { success: boolean };
  },

  createService: async (service: Service) => {
    await apiFetch('/api/services', { method: 'POST', body: JSON.stringify(service) });
    return { success: true };
  },
  updateService: async (service: Service) => {
    await apiFetch(`/api/services/${service.id}`, { method: 'PUT', body: JSON.stringify(service) });
    return { success: true };
  },
  deleteService: async (id: string) => {
    await apiFetch(`/api/services/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createVenue: async (venue: Venue) => {
    await apiFetch('/api/venues', { method: 'POST', body: JSON.stringify(venue) });
    return { success: true };
  },
  updateVenue: async (venue: Venue) => {
    await apiFetch(`/api/venues/${venue.id}`, { method: 'PUT', body: JSON.stringify(venue) });
    return { success: true };
  },
  deleteVenue: async (id: string) => {
    await apiFetch(`/api/venues/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createAddOn: async (addOn: AddOn) => {
    await apiFetch('/api/addons', { method: 'POST', body: JSON.stringify(addOn) });
    return { success: true };
  },
  updateAddOn: async (addOn: AddOn) => {
    await apiFetch(`/api/addons/${addOn.id}`, { method: 'PUT', body: JSON.stringify(addOn) });
    return { success: true };
  },
  deleteAddOn: async (id: string) => {
    await apiFetch(`/api/addons/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createUserGroup: async (group: UserGroup) => {
    await apiFetch('/api/user-groups', { method: 'POST', body: JSON.stringify(group) });
    return { success: true };
  },
  updateUserGroup: async (group: UserGroup) => {
    await apiFetch(`/api/user-groups/${group.id}`, {
      method: 'PATCH',
      body: JSON.stringify(group)
    });
    return { success: true };
  },
  deleteUserGroup: async (id: string) => {
    await apiFetch(`/api/user-groups/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createQuestionnaire: async (questionnaire: QuestionnaireTemplate) => {
    await apiFetch('/api/questionnaires', { method: 'POST', body: JSON.stringify(questionnaire) });
    return { success: true };
  },
  updateQuestionnaire: async (questionnaire: QuestionnaireTemplate) => {
    await apiFetch(`/api/questionnaires/${questionnaire.id}`, {
      method: 'PUT',
      body: JSON.stringify(questionnaire)
    });
    return { success: true };
  },
  deleteQuestionnaire: async (id: string) => {
    await apiFetch(`/api/questionnaires/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  createCustomer: async (customer: Customer) => {
    await apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(customer) });
    return { success: true };
  },
  updateCustomer: async (customer: Customer) => {
    await apiFetch(`/api/customers/${customer.id}`, {
      method: 'PATCH',
      body: JSON.stringify(customer)
    });
    return { success: true };
  },
  deleteCustomer: async (id: string) => {
    await apiFetch(`/api/customers/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  updateCompany: async (company: Partial<Company> & { id: string }) => {
    const payload = buildUpdateCompanyPayload(company);
    await apiFetch(`/api/companies/${company.id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    return { success: true };
  },

  deleteCompany: async (id: string) => {
    await apiFetch(`/api/companies/${id}`, { method: 'DELETE' });
    return { success: true };
  },

  updateSystemConfig: async (config: SystemConfigUpdatePayload) => {
    await apiFetch('/api/config', {
      method: 'POST',
      body: JSON.stringify(config)
    });
    return { success: true };
  },
  startSageOauthConnection: async (): Promise<SageOauthStartResponse> => {
    const data = await apiFetch('/api/config/sage/oauth/start');
    return toCamelCase(data || {}) as SageOauthStartResponse;
  },

  updateBookingStatus: async (id: string, status: string) => {
    await apiFetch(`/api/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
    return { success: true };
  },

  updateUserProfile: async (user: Partial<User> & { id: string }) => {
    await apiFetch(`/api/users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify(user)
    });
    return { success: true };
  },

  updateOwnSignature: async (userId: string, signature: string) => {
    await apiFetch(`/api/users/${userId}/signature`, {
      method: 'PATCH',
      body: JSON.stringify({ signature })
    });
    return { success: true };
  },

  updateOwnName: async (userId: string, name: string): Promise<{ name: string }> => {
    const data = await apiFetch(`/api/users/${userId}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ name })
    });
    return { name: data?.name || name.trim() };
  },

  getCertificateSigners: async (): Promise<{ signers: CertificateSigner[] }> => {
    const data = await apiFetch('/api/staff/users/signers');
    return {
      signers: toCamelArray<CertificateSigner>(data.signers || [])
    };
  },

  issueCertificate: async (cert: MedicalCertificate) => {
    await apiFetch('/api/certificates', {
      method: 'POST',
      body: JSON.stringify(cert)
    });
    return { success: true };
  },

  revokeCertificate: async (
    certificateId: string,
    reason?: string
  ): Promise<{ certificate: MedicalCertificate }> => {
    const id = String(certificateId || '').trim();
    if (!id) {
      throw new Error('Certificate ID is required');
    }
    const data = await apiFetch(`/api/staff/certificates/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ? String(reason).trim() : null })
    });
    return {
      certificate: toCamelCase(data.certificate || {}) as MedicalCertificate
    };
  },

  amendCertificate: async (
    certificateId: string,
    fields: Partial<
      Pick<
        MedicalCertificate,
        | 'validUntil'
        | 'outcome'
        | 'restrictions'
        | 'advice'
        | 'technicianName'
        | 'technicianSignature'
        | 'jobTitle'
      >
      // null clears a drug result that should never have been recorded;
      // omitting the key leaves the stored result untouched.
    > & { drugTestOutcome?: MedicalCertificate['drugTestOutcome'] | null }
  ): Promise<{ certificate: MedicalCertificate }> => {
    const id = String(certificateId || '').trim();
    if (!id) {
      throw new Error('Certificate ID is required');
    }
    const data = await apiFetch(`/api/staff/certificates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(fields || {})
    });
    return {
      certificate: toCamelCase(data.certificate || {}) as MedicalCertificate
    };
  },

  submitQuestionnaire: async (submission: QuestionnaireSubmission) => {
    await apiFetch('/api/submissions', {
      method: 'POST',
      body: JSON.stringify(submission)
    });
    return { success: true };
  },

  editSubmissionAnswers: async (
    submissionId: string,
    answers: Record<string, string>
  ): Promise<{ success: boolean }> => {
    return apiFetch(`/api/customer/submissions/${submissionId}/answers`, {
      method: 'PATCH',
      body: JSON.stringify({ answers })
    });
  },

  saveTechnicianComments: async (
    submissionId: string,
    comments: Record<string, string>
  ): Promise<{ success: boolean }> => {
    const data = await apiFetch(`/api/technician/submissions/${submissionId}/comments`, {
      method: 'PATCH',
      body: JSON.stringify({ comments })
    });
    return data as { success: boolean };
  },

  saveHealthAssessment: async (
    appointmentId: string,
    data: HealthAssessment
  ): Promise<{ healthAssessment: HealthAssessment }> => {
    const result = await apiFetch(`/api/staff/bookings/${appointmentId}/health-assessment`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    return result as { healthAssessment: HealthAssessment };
  },

  uploadPaperQuestionnaire: async (
    file: File,
    appointmentId: string,
    customerEmail: string
  ): Promise<QuestionnaireSubmission> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    const authHeader: Record<string, string> = {};
    if (session?.access_token) {
      authHeader.Authorization = `Bearer ${session.access_token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appointmentId', appointmentId);
    formData.append('customerEmail', customerEmail);
    const response = await fetch(`${API_BASE}/api/technician/submissions/upload-paper`, {
      method: 'POST',
      headers: authHeader,
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response.status, data, response.statusText));
    }
    return data as QuestionnaireSubmission;
  },

  removePaperQuestionnaire: async (
    submissionId: string,
    { override = false }: { override?: boolean } = {}
  ): Promise<void> => {
    const path = override
      ? `/api/technician/submissions/${submissionId}/paper/override`
      : `/api/technician/submissions/${submissionId}/paper`;
    await apiFetch(path, { method: 'DELETE' });
  },

  uploadAppointmentDocument: async (
    file: File,
    appointmentId: string,
    documentType: AppointmentDocumentType
  ): Promise<AppointmentDocument> => {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    const authHeader: Record<string, string> = {};
    if (session?.access_token) {
      authHeader.Authorization = `Bearer ${session.access_token}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    const response = await fetch(
      `${API_BASE}/api/technician/appointments/${appointmentId}/documents`,
      {
        method: 'POST',
        headers: authHeader,
        body: formData
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response.status, data, response.statusText));
    }
    return data as AppointmentDocument;
  },

  listAppointmentDocuments: async (appointmentId: string): Promise<AppointmentDocument[]> => {
    const data = await apiFetch(`/api/technician/appointments/${appointmentId}/documents`);
    return ((data as { items?: AppointmentDocument[] }).items || []) as AppointmentDocument[];
  },

  removeAppointmentDocument: async (documentId: string): Promise<void> => {
    await apiFetch(`/api/technician/documents/${documentId}`, { method: 'DELETE' });
  },

  updateCompanyPassword: async (companyId: string, memberId: string, pass: string) => {
    const { error: authError } = await supabase.auth.updateUser({ password: pass });
    if (authError) throw authError;

    if (memberId !== 'primary') {
      await apiFetch(`/api/company-users/${memberId}/password-updated`, { method: 'POST' });
    }
    return { success: true };
  },

  updateCustomerPassword: async (customerId: string, pass: string) => {
    const { error: authError } = await supabase.auth.updateUser({ password: pass });
    if (authError) throw authError;

    await apiFetch(`/api/customers/${customerId}/password-updated`, { method: 'POST' });
    return { success: true };
  },

  verifyRecoveryCode: async (email: string, code: string): Promise<void> => {
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type: 'recovery'
    });
    if (error) {
      throw new Error('Invalid or expired code. Please check your email or request a new one.');
    }
  },

  verifyCustomerSignInCode: async (email: string, code: string): Promise<void> => {
    const data = (await apiFetchPublic('/api/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        code: code.trim()
      })
    })) as { accessToken?: string; refreshToken?: string };

    if (!data?.accessToken || !data?.refreshToken) {
      throw new Error('Invalid or expired code. Please check your email or request a new one.');
    }

    const { error } = await supabase.auth.setSession({
      access_token: data.accessToken,
      refresh_token: data.refreshToken
    });
    if (error) {
      throw new Error('Invalid or expired code. Please check your email or request a new one.');
    }
  },

  requestPasswordReset: async (email: string, redirectTo?: string) => {
    await apiFetch('/api/auth/password-reset', {
      method: 'POST',
      body: JSON.stringify({
        email,
        redirectTo: redirectTo || `${window.location.origin}${window.location.pathname}?mode=reset`
      })
    });
    return { success: true };
  },

  requestCustomerLoginLink: async (email: string, redirectTo?: string) => {
    const data = await apiFetch('/api/auth/customer-login-link', {
      method: 'POST',
      body: JSON.stringify({
        email,
        redirectTo:
          redirectTo ||
          `${window.location.origin}${window.location.pathname}${window.location.search}`
      })
    });
    return data || { success: true };
  },

  reportLinkEvent: async (event: {
    kind: 'questionnaire_link';
    appointmentRef?: string;
    sessionState: string;
    routeServed: string;
  }): Promise<void> => {
    await apiFetchPublic('/api/auth/link-events', {
      method: 'POST',
      body: JSON.stringify(event)
    });
  }
};
